import React, { useEffect, useRef, useState } from "react";
import {
  buildMapboxStyleUrl,
  hasCoordinates,
  loadMapboxGlJs,
  type MapboxMapInstance,
  type MapboxMarkerInstance,
} from "../mapbox";
import type { VerifiedAddress } from "../types";
import styles from "./InteractiveClinicMap.module.css";

type InteractiveClinicMapProps = {
  address: VerifiedAddress;
  className?: string;
  initialZoom?: number;
  showControls?: boolean;
  showFullscreenControl?: boolean;
};

const InteractiveClinicMap = ({
  address,
  className = "",
  initialZoom = 14,
  showControls = true,
  showFullscreenControl = false,
}: InteractiveClinicMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const canRenderMap = hasCoordinates(address);

  useEffect(() => {
    if (!canRenderMap || !mapContainerRef.current) {
      return;
    }

    let isCancelled = false;
    let map: MapboxMapInstance | null = null;
    let marker: MapboxMarkerInstance | null = null;

    setStatus("loading");

    loadMapboxGlJs()
      .then((mapboxgl) => {
        if (isCancelled || !mapContainerRef.current || !mapboxgl?.Map) {
          if (!isCancelled) {
            setStatus("error");
          }
          return;
        }

        map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: buildMapboxStyleUrl(),
          center: [address.longitude, address.latitude],
          zoom: initialZoom,
          attributionControl: true,
        });

        if (showControls) {
          map.addControl(new mapboxgl.NavigationControl(), "top-right");
        }

        if (showFullscreenControl) {
          map.addControl(new mapboxgl.FullscreenControl(), "top-right");
        }

        const popup = address.formattedAddress.trim()
          ? new mapboxgl.Popup({ offset: 22 }).setText(address.formattedAddress)
          : null;

        marker = new mapboxgl.Marker({ color: "#2479f0" })
          .setLngLat([address.longitude, address.latitude]);

        if (popup) {
          marker.setPopup(popup);
        }

        marker.addTo(map);

        map.on("load", () => {
          if (!isCancelled) {
            setStatus("ready");
          }
        });

        map.on("error", () => {
          if (!isCancelled) {
            setStatus("error");
          }
        });
      })
      .catch(() => {
        if (!isCancelled) {
          setStatus("error");
        }
      });

    return () => {
      isCancelled = true;
      marker?.remove?.();
      map?.remove?.();
    };
  }, [
    address.formattedAddress,
    address.latitude,
    address.longitude,
    canRenderMap,
    initialZoom,
    showControls,
    showFullscreenControl,
  ]);

  const overlayMessage = !canRenderMap
    ? "Interactive map becomes available when the selected address returns coordinates."
    : status === "loading"
      ? "Loading interactive map..."
      : status === "error"
        ? "We couldn't load the interactive map."
        : "";

  return (
    <div className={`${styles.root} ${className}`.trim()}>
      {overlayMessage ? (
        <div className={styles.overlay}>
          <span className={styles.badge}>{overlayMessage}</span>
        </div>
      ) : null}
      <div
        ref={mapContainerRef}
        className={styles.canvas}
        aria-label={`Interactive map for ${address.formattedAddress}`}
      />
    </div>
  );
};

export default InteractiveClinicMap;
